/**
 * DealsFilterToolbar Component
 * 
 * A comprehensive filter toolbar for the deals table.
 * 
 * Key Features:
 * - Quick filter buttons for Deal Stages (from Supabase crm_deal_stages)
 * - Quick filter buttons for Tags (from Supabase crm_tags)
 * - At-Risk toggle filter
 * - Active filter chips that can be removed
 * - Advanced filter builder for complex queries
 * - Smart Lists integration for saved filter configurations
 * 
 * NOTE: Deal Stages and Tags are loaded from CrmSettingsContext
 * which reads from Supabase (admin-managed)
 */

import React, { useState, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  HStack,
  VStack,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuOptionGroup,
  MenuGroup,
  MenuDivider,
  Badge,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  Wrap,
  WrapItem,
  Collapse,
  useDisclosure,
  useColorModeValue,
  Portal,
  Input,
  InputGroup,
  InputLeftElement,
  Checkbox,
  Icon,
} from '@chakra-ui/react';
import {
  FiChevronDown,
  FiX,
  FiTag,
  FiAlertTriangle,
  FiBriefcase,
  FiSliders,
  FiSearch,
  FiLayers,
  FiEyeOff,
  FiSlash,
  FiSend,
} from 'react-icons/fi';
import { FilterBuilder } from './Filters';
import SmartListSelector from './SmartListSelector';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { useCampaignFilterOptions } from '../../hooks/useCampaignFilterOptions';
import { DEAL_PARTY_ROLES } from '../../constants/crmConstants';

// DNC quick-filter fields (each is an independent boolean filter on crm_deals)
const DNC_QUICK_FIELDS = ['dnc', 'dncCall', 'dncEmail', 'dncSms'];

/**
 * TagsFilterDropdown - Searchable multi-select for tags
 */
function TagsFilterDropdown({ activeTags, onToggleTag, availableTags, activeBg }) {
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const emptyTagColor = useColorModeValue('gray.400', 'gray.500');
  // Controlled so opening can reset the search and focus the input
  const { isOpen, onOpen, onClose } = useDisclosure();
  const searchInputRef = useRef(null);

  // Filter tags by search term (label falls back to value so a registry row
  // without a label can never crash the toolbar)
  const filteredTags = useMemo(() => {
    if (!tagSearchTerm.trim()) return availableTags;
    const term = tagSearchTerm.toLowerCase();
    return availableTags.filter(tag =>
      (tag.label || tag.value || '').toLowerCase().includes(term) ||
      (tag.value || '').toLowerCase().includes(term)
    );
  }, [availableTags, tagSearchTerm]);

  const handleMenuOpen = () => {
    setTagSearchTerm('');
    onOpen();
    // Chakra focuses the MenuList on open — steal focus back so the user can
    // type immediately
    setTimeout(() => searchInputRef.current?.focus(), 60);
  };

  const handleSearchKeyDown = (e) => {
    // Let Escape bubble so the menu closes; keep everything else away from
    // Chakra's menu typeahead/arrow-key handling, which would move focus to a
    // matching MenuItem mid-word
    if (e.key === 'Escape') return;
    e.stopPropagation();
    if (e.key === 'Enter' && filteredTags.length > 0) {
      onToggleTag(filteredTags[0].value);
    }
  };

  const handleSearchBlur = (e) => {
    // Chakra's useMenuItem focuses the MenuList container whenever an item
    // mounts while no item is focused — e.g. results reappearing after the
    // term is corrected. Blurring to the container is never a user action
    // (users click items or outside), so take focus back. Deferred: this blur
    // fires inside Chakra's focus() call, and a synchronous refocus would be
    // overwritten when that call completes.
    if (e.relatedTarget?.getAttribute?.('role') === 'menu') {
      const input = e.target;
      setTimeout(() => input.focus(), 0);
    }
  };

  const handleMenuListFocus = (e) => {
    // By the time the container's focus event fires, focus has already been
    // applied, so redirecting to the search box here is synchronous and
    // leaves no gap where a keystroke could land on the menu itself.
    if (e.target === e.currentTarget) {
      searchInputRef.current?.focus();
    }
  };

  return (
    <Menu
      closeOnSelect={false}
      autoSelect={false}
      isOpen={isOpen}
      onOpen={handleMenuOpen}
      onClose={onClose}
    >
      <MenuButton
        as={Button}
        size="sm"
        variant={activeTags.length > 0 ? 'solid' : 'outline'}
        colorScheme={activeTags.length > 0 ? 'purple' : 'gray'}
        leftIcon={<FiTag />}
        rightIcon={<FiChevronDown />}
      >
        Tags
        {activeTags.length > 0 && (
          <Badge ml={1} colorScheme="white" bg="purple.600" color="white" fontSize="xs">
            {activeTags.length}
          </Badge>
        )}
      </MenuButton>
      <Portal>
        <MenuList
          zIndex={1500}
          maxH="400px"
          overflowY="auto"
          minW="280px"
          onFocus={handleMenuListFocus}
        >
          {/* Search Input */}
          <Box px={3} py={2}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                ref={searchInputRef}
                placeholder="Search tags..."
                value={tagSearchTerm}
                onChange={(e) => setTagSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchBlur}
              />
            </InputGroup>
          </Box>
          
          <MenuDivider />
          
          {/* Tags List */}
          {filteredTags.length > 0 ? (
            filteredTags.map(tag => {
              const hasNoDeals = (tag.dealCount || 0) === 0;
              return (
                <MenuItem
                  key={tag.value}
                  onClick={() => onToggleTag(tag.value)}
                  bg={activeTags.includes(tag.value) ? activeBg : undefined}
                >
                  <HStack justify="space-between" width="100%">
                    <HStack spacing={2}>
                      <Checkbox 
                        isChecked={activeTags.includes(tag.value)} 
                        pointerEvents="none"
                        size="sm"
                      />
                      <Text fontSize="sm" color={hasNoDeals ? emptyTagColor : undefined}>
                        {tag.displayLabel || tag.label || tag.value}
                      </Text>
                    </HStack>
                    {/* Deal count makes it obvious when a tag matches nothing, and
                        why adding a tag to a selection may barely move the total */}
                    <Text fontSize="xs" color={emptyTagColor} flexShrink={0}>
                      {tag.dealCount || 0}
                    </Text>
                  </HStack>
                </MenuItem>
              );
            })
          ) : (
            // Plain Box, NOT a MenuItem: a MenuItem mounting mid-keystroke
            // makes Chakra move focus to the MenuList (see handleSearchBlur)
            <Box px={3} py={2}>
              <Text fontSize="sm" color="gray.500">No tags found</Text>
            </Box>
          )}
          
          {/* Selected count */}
          {activeTags.length > 0 && (
            <>
              <MenuDivider />
              <Box px={3} py={2}>
                <VStack align="start" spacing={1}>
                  <Badge colorScheme="purple">{activeTags.length} selected</Badge>
                  <Text fontSize="xs" color="gray.500">
                    Deals matching any selected tag. A deal with several of these
                    is still counted once, so the total is not the sum.
                  </Text>
                </VStack>
              </Box>
            </>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
}

/**
 * StageFilterDropdown - Multi-select for deal stages
 */
function StageFilterDropdown({ activeStages, onToggleStage, availableStages, activeBg }) {
  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        size="sm"
        variant={activeStages.length > 0 ? 'solid' : 'outline'}
        colorScheme={activeStages.length > 0 ? 'blue' : 'gray'}
        leftIcon={<FiLayers />}
        rightIcon={<FiChevronDown />}
      >
        Stage
        {activeStages.length > 0 && (
          <Badge ml={1} colorScheme="white" bg="blue.600" color="white" fontSize="xs">
            {activeStages.length}
          </Badge>
        )}
      </MenuButton>
      <Portal>
        <MenuList zIndex={1500} maxH="400px" overflowY="auto">
          <MenuGroup title="Filter by Stage">
            {(availableStages || []).map(stage => (
              <MenuItem
                key={stage.value}
                onClick={() => onToggleStage(stage.value)}
                bg={activeStages.includes(stage.value) ? activeBg : undefined}
              >
                <HStack justify="space-between" width="100%">
                  <HStack spacing={2}>
                    <Checkbox 
                      isChecked={activeStages.includes(stage.value)} 
                      pointerEvents="none"
                      size="sm"
                    />
                    <Text fontSize="sm">{stage.label}</Text>
                  </HStack>
                  {stage.color && (
                    <Badge colorScheme={stage.color} size="sm" fontSize="xs">
                      {stage.color}
                    </Badge>
                  )}
                </HStack>
              </MenuItem>
            ))}
          </MenuGroup>
          
          {/* Selected count */}
          {activeStages.length > 0 && (
            <>
              <MenuDivider />
              <Box px={3} py={2}>
                <Badge colorScheme="blue">{activeStages.length} selected</Badge>
              </Box>
            </>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
}

/**
 * CampaignFilterDropdown - Searchable multi-select for campaign enrollment.
 *
 * Options come from useCampaignFilterOptions (active templates + live
 * currently-enrolled contact counts). A deal matches when its CONTACT is
 * currently enrolled — enrollment is contact-scoped. Focus/search handling is
 * a clone of TagsFilterDropdown above; see its comments for the Chakra quirks.
 */
function CampaignFilterDropdown({ activeCampaigns, onToggleCampaign, availableCampaigns, activeBg, isLoading }) {
  const [searchTerm, setSearchTerm] = useState('');
  const mutedColor = useColorModeValue('gray.400', 'gray.500');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const searchInputRef = useRef(null);

  const filteredCampaigns = useMemo(() => {
    if (!searchTerm.trim()) return availableCampaigns;
    const term = searchTerm.toLowerCase();
    return availableCampaigns.filter(c => (c.label || '').toLowerCase().includes(term));
  }, [availableCampaigns, searchTerm]);

  const handleMenuOpen = () => {
    setSearchTerm('');
    onOpen();
    setTimeout(() => searchInputRef.current?.focus(), 60);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') return;
    e.stopPropagation();
    if (e.key === 'Enter' && filteredCampaigns.length > 0) {
      onToggleCampaign(filteredCampaigns[0].value);
    }
  };

  const handleSearchBlur = (e) => {
    if (e.relatedTarget?.getAttribute?.('role') === 'menu') {
      const input = e.target;
      setTimeout(() => input.focus(), 0);
    }
  };

  const handleMenuListFocus = (e) => {
    if (e.target === e.currentTarget) {
      searchInputRef.current?.focus();
    }
  };

  return (
    <Menu
      closeOnSelect={false}
      autoSelect={false}
      isOpen={isOpen}
      onOpen={handleMenuOpen}
      onClose={onClose}
    >
      <MenuButton
        as={Button}
        size="sm"
        variant={activeCampaigns.length > 0 ? 'solid' : 'outline'}
        colorScheme={activeCampaigns.length > 0 ? 'teal' : 'gray'}
        leftIcon={<FiSend />}
        rightIcon={<FiChevronDown />}
        isLoading={isLoading}
      >
        Campaign
        {activeCampaigns.length > 0 && (
          <Badge ml={1} colorScheme="white" bg="teal.600" color="white" fontSize="xs">
            {activeCampaigns.length}
          </Badge>
        )}
      </MenuButton>
      <Portal>
        <MenuList
          zIndex={1500}
          maxH="400px"
          overflowY="auto"
          minW="300px"
          onFocus={handleMenuListFocus}
        >
          {/* Search Input */}
          <Box px={3} py={2}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                ref={searchInputRef}
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchBlur}
              />
            </InputGroup>
          </Box>

          <MenuDivider />

          {/* Campaign List */}
          {filteredCampaigns.length > 0 ? (
            filteredCampaigns.map(campaign => {
              const hasNoEnrollees = (campaign.activeCount || 0) === 0;
              return (
                <MenuItem
                  key={campaign.value}
                  onClick={() => onToggleCampaign(campaign.value)}
                  bg={activeCampaigns.includes(campaign.value) ? activeBg : undefined}
                >
                  <HStack justify="space-between" width="100%">
                    <HStack spacing={2} minW={0}>
                      <Checkbox
                        isChecked={activeCampaigns.includes(campaign.value)}
                        pointerEvents="none"
                        size="sm"
                      />
                      <Text fontSize="sm" color={hasNoEnrollees ? mutedColor : undefined} noOfLines={1}>
                        {campaign.label}
                      </Text>
                    </HStack>
                    {/* Enrolled-contact count makes it obvious when a campaign matches nothing */}
                    <Text fontSize="xs" color={mutedColor} flexShrink={0}>
                      {campaign.activeCount || 0}
                    </Text>
                  </HStack>
                </MenuItem>
              );
            })
          ) : (
            // Plain Box, NOT a MenuItem (see TagsFilterDropdown)
            <Box px={3} py={2}>
              <Text fontSize="sm" color="gray.500">No campaigns found</Text>
            </Box>
          )}

          <MenuDivider />
          <Box px={3} py={2}>
            <VStack align="start" spacing={1}>
              {activeCampaigns.length > 0 && (
                <Badge colorScheme="teal">{activeCampaigns.length} selected</Badge>
              )}
              <Text fontSize="xs" color="gray.500">
                Currently enrolled only — shows deals whose contact has campaign
                steps still queued (pending/processing). Counts are contacts.
              </Text>
            </VStack>
          </Box>
        </MenuList>
      </Portal>
    </Menu>
  );
}

/**
 * Get human-readable label for a filter
 * @param {Object} filter - The filter object
 * @param {Array} dealStages - Deal stages from CRM settings
 * @param {Array} dealTags - Tags from CRM settings
 * @param {Function|null} getCampaignName - Resolves a campaign template id to its name
 */
function getFilterLabel(filter, dealStages = [], dealTags = [], getCampaignName = null) {
  const { field, operator, value } = filter;
  
  // Stage filter
  if (field === 'stage') {
    // Handle "not_in" operator for hiding stages (like "Hide Lost")
    if (operator === 'not_in') {
      const stages = Array.isArray(value) ? value : [value];
      // Only the exact Hide Lost toggle shape gets the short label; a multi-stage
      // exclusion (e.g. a smart list hiding all terminal stages) must list them
      // all or the chip understates what is hidden.
      if (stages.length === 1 && stages[0] === 'deal-lost') {
        return 'Hiding Lost Deals';
      }
      const labels = stages.map(v => {
        const opt = dealStages.find(o => o.value === v);
        return opt ? opt.label : v;
      });
      return `Excluding: ${labels.join(', ')}`;
    }
    
    const stages = Array.isArray(value) ? value : [value];
    const labels = stages.map(v => {
      const opt = dealStages.find(o => o.value === v);
      return opt ? opt.label : v;
    });
    return `Stage: ${labels.join(', ')}`;
  }
  
  // Tag filter
  if (field === 'tags') {
    const tags = Array.isArray(value) ? value : [value];
    const labels = tags.map(v => {
      const tag = dealTags.find(t => t.value === v);
      return tag ? (tag.displayLabel || tag.label || v) : v;
    });
    const prefix = operator === 'contains_all' ? 'Tags (all of)'
      : operator === 'not_contains_any' ? 'Tags (none of)'
      : 'Tags';
    return `${prefix}: ${labels.join(', ')}`;
  }

  // Deal Team role filter (values are role slugs — resolve via DEAL_PARTY_ROLES)
  if (field === 'dealTeamRoles') {
    if (operator === 'array_is_empty') return 'No deal team';
    if (operator === 'is_not_empty') return 'Has a deal team';
    const roles = Array.isArray(value) ? value : [value];
    const labels = roles.map(v => {
      const cfg = DEAL_PARTY_ROLES.find(r => r.value === v);
      return cfg ? cfg.label : v;
    });
    const prefix = operator === 'not_contains_any' ? 'Team missing'
      : operator === 'contains_all' ? 'Team has (all of)'
      : 'Team has';
    return `${prefix}: ${labels.join(', ')}`;
  }

  // Campaign enrollment filter (values are template ids — resolve to names)
  if (field === 'activeCampaigns') {
    if (operator === 'array_is_empty') return 'In no campaign';
    if (operator === 'is_not_empty') return 'In any campaign';
    const ids = Array.isArray(value) ? value : [value];
    const labels = ids.map(v => (getCampaignName ? getCampaignName(v) : v));
    const prefix = operator === 'not_contains_any' ? 'Not enrolled in'
      : operator === 'contains_all' ? 'Enrolled in (all of)'
      : 'Enrolled in';
    return `${prefix}: ${labels.join(', ')}`;
  }
  
  // At-risk filter
  if (field === 'isAtRisk') {
    return 'At-Risk Deals';
  }

  // Record scope filter (company vs personal records)
  if (field === 'recordScope') {
    const scopes = Array.isArray(value) ? value : [value];
    const labels = scopes.map(v => (v === 'company' ? 'Company' : 'Personal'));
    return operator === 'not_in'
      ? `Scope not: ${labels.join(', ')}`
      : `Scope: ${labels.join(', ')}`;
  }

  // DNC filters — is_true shows only flagged deals, is_false excludes them
  const DNC_LABELS = { dnc: 'Everything', dncCall: 'Call', dncEmail: 'Email', dncSms: 'SMS' };
  if (DNC_LABELS[field]) {
    return operator === 'is_false'
      ? `Excluding DNC: ${DNC_LABELS[field]}`
      : `DNC: ${DNC_LABELS[field]}`;
  }
  
  // Amount filter
  if (field === 'amount') {
    if (operator === 'gte') return `Amount ≥ $${value?.toLocaleString()}`;
    if (operator === 'lte') return `Amount ≤ $${value?.toLocaleString()}`;
    if (operator === 'between' && Array.isArray(value)) {
      return `Amount: $${value[0]?.toLocaleString()} - $${value[1]?.toLocaleString()}`;
    }
  }
  
  // Date filters
  if (field === 'estimatedClosingDate' || field === 'createdAt' || field === 'updatedAt' || field === 'nextActionDue' || field === 'appDate') {
    const fieldLabels = {
      estimatedClosingDate: 'Est. Close',
      createdAt: 'Created',
      updatedAt: 'Updated',
      nextActionDue: 'Next Action Due',
      appDate: 'App Date',
    };
    if (operator === 'date_relative') {
      const presetLabels = {
        today: 'Today',
        yesterday: 'Yesterday',
        last_7_days: 'Last 7 Days',
        last_14_days: 'Last 14 Days',
        last_30_days: 'Last 30 Days',
        last_90_days: 'Last 90 Days',
        this_month: 'This Month',
        last_month: 'Last Month',
        this_year: 'This Year',
        closing_this_week: 'This Week',
        closing_this_month: 'This Month',
      };
      return `${fieldLabels[field] || field}: ${presetLabels[value] || value}`;
    }
  }
  
  // Source filter
  if (field === 'source' || field === 'sourceMedium') {
    const sources = Array.isArray(value) ? value : [value];
    return `Source: ${sources.join(', ')}`;
  }
  
  // Product filter
  if (field === 'product') {
    const products = Array.isArray(value) ? value : [value];
    return `Product: ${products.join(', ')}`;
  }
  
  // Generic fallback
  return `${field}: ${value}`;
}

/**
 * DealsFilterToolbar Component
 */
export default function DealsFilterToolbar({
  // Current filters from hook
  filters = [],
  onFiltersChange,
  
  // Smart Lists props
  smartLists = [],
  personalLists = [],
  globalLists = [],
  selectedSmartListId,
  onSelectSmartList,
  onCreateSmartList,
  onUpdateSmartList,
  onDeleteSmartList,
  onDuplicateSmartList,
  onSetDefaultSmartList,
  onUnsetDefaultSmartList,
  
  // Current sort & visible columns (for "Save Current Filters as List")
  currentSort = {},
  visibleColumns = [],
  
  // Loading state
  loading = false,

  // Optional extra controls rendered right of the Smart List button
  rightSlot = null,
}) {
  const { isOpen: isAdvancedOpen, onToggle: onAdvancedToggle } = useDisclosure();
  
  // Get current user context for tag visibility filtering
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  
  // Get admin-configured deal stages and tags from CRM Settings
  // Note: dealTags comes from Supabase crm_tags, dealStages from crm_deal_stages
  const { 
    contactTypes, 
    dealStages, 
    getVisibleContactTags, 
    getVisibleDealTags,
    loading: settingsLoading 
  } = useCrmSettings();
  
  // Get visibility-filtered tags based on current user's permissions
  const contactTags = useMemo(() => 
    getVisibleContactTags(currentUser?.uid, isCRMAdmin),
    [currentUser?.uid, isCRMAdmin, getVisibleContactTags]
  );
  
  const dealTags = useMemo(() => 
    getVisibleDealTags(currentUser?.uid, isCRMAdmin),
    [currentUser?.uid, isCRMAdmin, getVisibleDealTags]
  );

  // Campaign enrollment filter options (RPC-backed, live enrolled counts)
  const { campaignOptions, getCampaignName, loading: campaignsLoading } = useCampaignFilterOptions();
  
  const activeBg = useColorModeValue('blue.50', 'blue.900');

  // Clear All is a destructive escape hatch that users need to spot quickly, so
  // it gets a filled light-red chip rather than the ghost styling of its
  // neighbours. Both pairings clear WCAG AA comfortably.
  const clearAllBg = useColorModeValue('red.100', 'red.200');
  const clearAllColor = useColorModeValue('red.800', 'red.900');
  const clearAllHoverBg = useColorModeValue('red.200', 'red.300');
  
  // Stage selection is the `in` filter only. "Hide Lost" is a separate
  // stage/not_in filter, and the advanced builder can add stage/is_empty —
  // matching on field alone would show those as checked stages.
  const activeStages = useMemo(() => {
    const stageFilter = filters.find(f => f.field === 'stage' && f.operator === 'in');
    return stageFilter ? (Array.isArray(stageFilter.value) ? stageFilter.value : [stageFilter.value]) : [];
  }, [filters]);
  
  // Likewise, only the quick dropdown's contains_any filter counts as a tag
  // selection; a contains_all filter from the advanced builder must not be
  // silently rewritten into an OR.
  const activeTags = useMemo(() => {
    const tagFilter = filters.find(f => f.field === 'tags' && f.operator === 'contains_any');
    return tagFilter ? (Array.isArray(tagFilter.value) ? tagFilter.value : [tagFilter.value]) : [];
  }, [filters]);

  const activeCampaigns = useMemo(() => {
    const campaignFilter = filters.find(f => f.field === 'activeCampaigns' && f.operator === 'contains_any');
    return campaignFilter ? (Array.isArray(campaignFilter.value) ? campaignFilter.value : [campaignFilter.value]) : [];
  }, [filters]);
  
  // Check if at-risk filter is active
  const isAtRiskActive = useMemo(() => {
    return filters.some(f => f.field === 'isAtRisk' && f.operator === 'is_true');
  }, [filters]);

  // Company-records quick filter (admin-only chip; record_scope = 'company')
  const isCompanyScopeActive = useMemo(() => {
    return filters.some(f => f.field === 'recordScope' && f.operator === 'in');
  }, [filters]);
  
  // Check if "hide lost deals" filter is active
  const isHideLostActive = useMemo(() => {
    return filters.some(f => 
      f.field === 'stage' && 
      f.operator === 'not_in' && 
      Array.isArray(f.value) && 
      f.value.includes('deal-lost')
    );
  }, [filters]);
  
  /**
   * Toggle a stage filter
   */
  const handleToggleStage = (stageValue) => {
    const existingFilter = filters.find(f => f.field === 'stage' && f.operator === 'in');
    const currentStages = existingFilter 
      ? (Array.isArray(existingFilter.value) ? existingFilter.value : [existingFilter.value])
      : [];
    
    let newStages;
    if (currentStages.includes(stageValue)) {
      // Remove stage
      newStages = currentStages.filter(s => s !== stageValue);
    } else {
      // Add stage
      newStages = [...currentStages, stageValue];
    }
    
    // Replace only our own stage/in filter, leaving Hide Lost (stage/not_in) intact
    const otherFilters = filters.filter(f => !(f.field === 'stage' && f.operator === 'in'));
    if (newStages.length > 0) {
      onFiltersChange([
        ...otherFilters,
        { field: 'stage', operator: 'in', value: newStages },
      ]);
    } else {
      onFiltersChange(otherFilters);
    }
  };
  
  /**
   * Toggle a tag filter
   */
  const handleToggleTag = (tagValue) => {
    const existingFilter = filters.find(f => f.field === 'tags' && f.operator === 'contains_any');
    const currentTags = existingFilter 
      ? (Array.isArray(existingFilter.value) ? existingFilter.value : [existingFilter.value])
      : [];
    
    let newTags;
    if (currentTags.includes(tagValue)) {
      newTags = currentTags.filter(t => t !== tagValue);
    } else {
      newTags = [...currentTags, tagValue];
    }
    
    const otherFilters = filters.filter(f => !(f.field === 'tags' && f.operator === 'contains_any'));
    if (newTags.length > 0) {
      onFiltersChange([
        ...otherFilters,
        { field: 'tags', operator: 'contains_any', value: newTags },
      ]);
    } else {
      onFiltersChange(otherFilters);
    }
  };

  /**
   * Toggle a campaign enrollment filter (values are template ids).
   * Replaces only its own contains_any filter, leaving advanced-builder
   * operators on the same field intact — same pattern as handleToggleTag.
   */
  const handleToggleCampaign = (templateId) => {
    const existingFilter = filters.find(f => f.field === 'activeCampaigns' && f.operator === 'contains_any');
    const current = existingFilter
      ? (Array.isArray(existingFilter.value) ? existingFilter.value : [existingFilter.value])
      : [];

    const next = current.includes(templateId)
      ? current.filter(id => id !== templateId)
      : [...current, templateId];

    const otherFilters = filters.filter(f => !(f.field === 'activeCampaigns' && f.operator === 'contains_any'));
    if (next.length > 0) {
      onFiltersChange([
        ...otherFilters,
        { field: 'activeCampaigns', operator: 'contains_any', value: next },
      ]);
    } else {
      onFiltersChange(otherFilters);
    }
  };
  
  /**
   * Toggle at-risk filter
   */
  const handleToggleAtRisk = () => {
    if (isAtRiskActive) {
      onFiltersChange(filters.filter(f => f.field !== 'isAtRisk'));
    } else {
      onFiltersChange([
        ...filters.filter(f => f.field !== 'isAtRisk'),
        { field: 'isAtRisk', operator: 'is_true', value: true },
      ]);
    }
  };

  /**
   * Toggle the company-records filter (admin-only chip)
   */
  const handleToggleCompanyScope = () => {
    if (isCompanyScopeActive) {
      onFiltersChange(filters.filter(f => f.field !== 'recordScope'));
    } else {
      onFiltersChange([
        ...filters.filter(f => f.field !== 'recordScope'),
        { field: 'recordScope', operator: 'in', value: ['company'] },
      ]);
    }
  };

  // DNC quick filters — each category is an independent boolean filter.
  // "Show only" keeps just the flagged deals (is_true); "Exclude" hides
  // them (is_false). A category can be in one mode at a time.
  const activeDncShowFields = useMemo(
    () => DNC_QUICK_FIELDS.filter(field => filters.some(f => f.field === field && f.operator === 'is_true')),
    [filters]
  );
  const activeDncExcludeFields = useMemo(
    () => DNC_QUICK_FIELDS.filter(field => filters.some(f => f.field === field && f.operator === 'is_false')),
    [filters]
  );
  const applyDncSelections = (showFields, excludeFields) => {
    const others = filters.filter(f => !DNC_QUICK_FIELDS.includes(f.field));
    onFiltersChange([
      ...others,
      ...showFields.map(field => ({ field, operator: 'is_true', value: true })),
      ...excludeFields.map(field => ({ field, operator: 'is_false', value: false })),
    ]);
  };
  const handleDncShowChange = (selected) => {
    const sel = Array.isArray(selected) ? selected : [selected];
    // A field newly shown can't stay excluded
    applyDncSelections(sel, activeDncExcludeFields.filter(f => !sel.includes(f)));
  };
  const handleDncExcludeChange = (selected) => {
    const sel = Array.isArray(selected) ? selected : [selected];
    applyDncSelections(activeDncShowFields.filter(f => !sel.includes(f)), sel);
  };
  
  /**
   * Toggle "hide lost deals" filter
   */
  const handleToggleHideLost = () => {
    if (isHideLostActive) {
      // Remove the hide lost filter
      onFiltersChange(filters.filter(f => 
        !(f.field === 'stage' && f.operator === 'not_in')
      ));
    } else {
      // Add the hide lost filter
      onFiltersChange([
        ...filters.filter(f => !(f.field === 'stage' && f.operator === 'not_in')),
        { field: 'stage', operator: 'not_in', value: ['deal-lost'] },
      ]);
    }
  };
  
  /**
   * Remove a specific filter by its position in the list.
   *
   * Indexed rather than matched by field, because several filters can share a
   * field with different operators (e.g. stage/in alongside stage/not_in from
   * "Hide Lost"); removing by field would drop both.
   */
  const handleRemoveFilter = (indexToRemove) => {
    onFiltersChange(filters.filter((_, i) => i !== indexToRemove));
  };
  
  /**
   * Clear all filters
   */
  const handleClearAll = () => {
    onFiltersChange([]);
  };
  
  /**
   * Apply advanced filters
   */
  const handleApplyAdvancedFilters = (newFilters) => {
    onFiltersChange(newFilters);
  };
  
  const hasActiveFilters = filters.length > 0;
  
  return (
    <VStack align="stretch" spacing={3} width="100%">
      {/* Main Filter Row */}
      <HStack spacing={2} flexWrap="wrap" justify="space-between">
        {/* Left side: Quick Filters */}
        <HStack spacing={2} flexWrap="wrap">
          {/* Stage Dropdown - Uses admin-configured stages from CRM Settings */}
          <StageFilterDropdown
            activeStages={activeStages}
            onToggleStage={handleToggleStage}
            availableStages={dealStages || []}
            activeBg={activeBg}
          />
          
          {/* Tags Dropdown - Uses admin-configured tags from CRM Settings */}
          <TagsFilterDropdown
            activeTags={activeTags}
            onToggleTag={handleToggleTag}
            availableTags={dealTags || []}
            activeBg={activeBg}
          />

          {/* Campaign Dropdown - deals whose contact is currently enrolled */}
          <CampaignFilterDropdown
            activeCampaigns={activeCampaigns}
            onToggleCampaign={handleToggleCampaign}
            availableCampaigns={campaignOptions}
            activeBg={activeBg}
            isLoading={campaignsLoading}
          />
          
          {/* At-Risk Button */}
          <Button
            size="sm"
            variant={isAtRiskActive ? 'solid' : 'outline'}
            colorScheme={isAtRiskActive ? 'red' : 'gray'}
            leftIcon={<FiAlertTriangle />}
            onClick={handleToggleAtRisk}
          >
            At-Risk
          </Button>

          {/* Company records (admin-only quick chip) */}
          {isCRMAdmin && (
            <Button
              size="sm"
              variant={isCompanyScopeActive ? 'solid' : 'outline'}
              colorScheme={isCompanyScopeActive ? 'purple' : 'gray'}
              leftIcon={<FiBriefcase />}
              onClick={handleToggleCompanyScope}
            >
              Company
            </Button>
          )}

          {/* DNC Dropdown */}
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              size="sm"
              variant={activeDncShowFields.length + activeDncExcludeFields.length > 0 ? 'solid' : 'outline'}
              colorScheme={activeDncShowFields.length + activeDncExcludeFields.length > 0 ? 'red' : 'gray'}
              leftIcon={<FiSlash />}
              rightIcon={<FiChevronDown />}
            >
              DNC
            </MenuButton>
            <Portal>
              <MenuList minW="220px">
                <MenuOptionGroup
                  type="checkbox"
                  title="Show only (flagged)"
                  value={activeDncShowFields}
                  onChange={handleDncShowChange}
                >
                  <MenuItemOption value="dnc">Everything</MenuItemOption>
                  <MenuItemOption value="dncCall">Call / Phone</MenuItemOption>
                  <MenuItemOption value="dncEmail">Email</MenuItemOption>
                  <MenuItemOption value="dncSms">SMS</MenuItemOption>
                </MenuOptionGroup>
                <MenuDivider />
                <MenuOptionGroup
                  type="checkbox"
                  title="Exclude (hide flagged)"
                  value={activeDncExcludeFields}
                  onChange={handleDncExcludeChange}
                >
                  <MenuItemOption value="dnc">Everything</MenuItemOption>
                  <MenuItemOption value="dncCall">Call / Phone</MenuItemOption>
                  <MenuItemOption value="dncEmail">Email</MenuItemOption>
                  <MenuItemOption value="dncSms">SMS</MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </Portal>
          </Menu>
          
          {/* Hide Lost Deals Toggle */}
          <Button
            size="sm"
            variant={isHideLostActive ? 'solid' : 'outline'}
            colorScheme={isHideLostActive ? 'orange' : 'gray'}
            leftIcon={<FiEyeOff />}
            onClick={handleToggleHideLost}
          >
            Hide Lost
          </Button>
          
          {/* More Filters Button */}
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FiSliders />}
            onClick={onAdvancedToggle}
            colorScheme={isAdvancedOpen ? 'blue' : 'gray'}
          >
            {isAdvancedOpen ? 'Hide Filters' : 'More Filters'}
          </Button>
        </HStack>
        
        {/* Right side: Smart Lists & Clear */}
        <HStack spacing={2}>
          {/* Smart Lists */}
          <SmartListSelector
            smartLists={smartLists}
            personalLists={personalLists}
            globalLists={globalLists}
            selectedListId={selectedSmartListId}
            onSelectList={onSelectSmartList}
            onCreateList={onCreateSmartList}
            onUpdateList={onUpdateSmartList}
            onDeleteList={onDeleteSmartList}
            onDuplicateList={onDuplicateSmartList}
            onSetDefault={onSetDefaultSmartList}
            onUnsetDefault={onUnsetDefaultSmartList}
            currentFilters={filters}
            currentSort={currentSort}
            visibleColumns={visibleColumns}
            entityType="deals"
            loading={loading}
          />

          {rightSlot}

          {/* Clear All */}
          {hasActiveFilters && (
            <Button
              size="md"
              leftIcon={<FiX />}
              onClick={handleClearAll}
              bg={clearAllBg}
              color={clearAllColor}
              fontWeight="semibold"
              _hover={{ bg: clearAllHoverBg }}
              _active={{ bg: clearAllHoverBg }}
            >
              Clear All
            </Button>
          )}
        </HStack>
      </HStack>
      
      {/* Active Filters Row */}
      {hasActiveFilters && (
        <Wrap spacing={2}>
          {filters.map((filter, index) => (
            <WrapItem key={`${filter.field}-${index}`}>
              <Tag
                size="md"
                variant="subtle"
                colorScheme={
                  filter.field === 'stage' && filter.operator === 'not_in' ? 'orange' :
                  filter.field === 'stage' ? 'blue' :
                  filter.field === 'tags' ? 'purple' :
                  filter.field === 'activeCampaigns' ? 'teal' :
                  filter.field === 'isAtRisk' ? 'red' :
                  'gray'
                }
              >
                <TagLabel>{getFilterLabel(filter, dealStages, dealTags, getCampaignName)}</TagLabel>
                <TagCloseButton onClick={() => handleRemoveFilter(index)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
      
      {/* Advanced Filter Builder */}
      <Collapse in={isAdvancedOpen}>
        <Box pt={2}>
          <FilterBuilder
            filters={filters}
            onFiltersChange={handleApplyAdvancedFilters}
            entityType="deals"
            contactTypes={contactTypes}
            contactTags={contactTags}
            dealTags={dealTags}
            dealStages={dealStages}
            campaignOptions={campaignOptions}
          />
        </Box>
      </Collapse>
    </VStack>
  );
}
